import { FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup } from '@mui/material';
import Grid from '@mui/material/Grid';
import React from 'react';
import { Controller } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { FormInputProps, IOption, ISelectInput, inputGrid } from './InputInterface';

interface StyledRadioWrapperProps {
  radioVariant?: 'outline' | 'outline-with-icon' | 'text';
  iconUrls: IOption[]; // Array of icon URLs for each radio option
}

const StyledRadioWrapper = styled.div<StyledRadioWrapperProps>`
  & .MuiFormControlLabel-root {
    border: 1px solid ${(props) => props.theme.colors.inputBorder};
    width: 100%;
    border-radius: 4px;
    margin-left: 0;
    height: 100%;
    padding-right: 10px;
  }

  ${(props) =>
    props.radioVariant === 'outline-with-icon' &&
    props.iconUrls &&
    css`
      .MuiFormControlLabel-root {
        display: flex;
        &::after {
          content: '';
          margin-left: auto;
          margin-right: 24px;
          margin-left: 10px;
          background-repeat: no-repeat;
          background-size: 100%;

          @media (max-width: 1650px) {
            margin-right: 16px;
          }
        }
      }

      .MuiTypography-root {
        flex: 1 1 0;
      }

      .MuiGrid-item {
        ${(props.iconUrls || []).map(
          (url, index) => css`
            &:nth-child(${index + 1}) {
              .MuiFormControlLabel-root {
                &::after {
                  background-image: url(${url?.icon});
                  width: 36px;
                  height: 21px;

                  @media (max-width: 1650px) {
                    width: 25px;
                    height: 15px;
                  }
                }
              }
            }
          `
        )}
      }
    `}
`;

const DabaduRadio = styled(FormControl)`
  & .MuiTypography-root {
    font-family: ${(props) => props.theme.fonts.primary};
  }
  & .MuiSvgIcon-root {
    fill: ${(props) => props.theme.colors.primaryColor};
  }
  & .MuiFormHelperText-root {
    order: -1;
    margin-left: 0;
  }
`;

const generateRadioOptions = (options: IOption[], itemPerRow: inputGrid, radioVariant: string) => {
  return (
    <>
      {radioVariant === 'text' ? (
        options.map((singleOption) => (
          <>
            <FormControlLabel
              key={singleOption.value}
              value={singleOption.value}
              label={singleOption.label}
              control={<Radio />}
              className={`${singleOption?.isCorrect ? 'highlighted-text' : ''}`}
            />
          </>
        ))
      ) : (
        <Grid container rowSpacing={3} columnSpacing={2} justifyContent={'center'}>
          {options.map((singleOption) => (
            <Grid
              item
              xs={itemPerRow.xs}
              sm={itemPerRow.sm}
              md={itemPerRow.md}
              lg={itemPerRow.lg}
              xl={itemPerRow.xl}
              key={singleOption.value}
            >
              <FormControlLabel
                value={singleOption.value}
                label={singleOption.label}
                className={`${singleOption?.isCorrect ? 'highlighted-text' : ''}`}
                control={<Radio />}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export const InputRadio: React.FC<FormInputProps & ISelectInput> = ({
  name,
  control,
  label,
  options,
  rules,
  directionRow = true,
  triggerValidationForField,
  itemPerRow = { xs: 12 },
  radioVariant = 'text',
}) => {
  return (
    <>
      {radioVariant === 'outline' || radioVariant === 'outline-with-icon' ? (
        <StyledRadioWrapper radioVariant={radioVariant} iconUrls={options}>
          {renderRadioComponent({
            name,
            control,
            label,
            options,
            rules,
            directionRow,
            triggerValidationForField,
            itemPerRow,
            radioVariant,
          })}
        </StyledRadioWrapper>
      ) : (
        renderRadioComponent({
          name,
          control,
          label,
          options,
          rules,
          directionRow,
          triggerValidationForField,
          itemPerRow,
          radioVariant,
        })
      )}
    </>
  );
};

const renderRadioComponent = ({
  name,
  control,
  label,
  options,
  rules,
  directionRow = true,
  triggerValidationForField,
  itemPerRow = { xs: 12 },
  radioVariant = 'text',
}: FormInputProps & ISelectInput): JSX.Element => (
  <div className='mui-radio-wrapper mb-3'>
    {label && <FormLabel>{label}</FormLabel>}
    <DabaduRadio component='fieldset' required={Boolean(rules)} error={Boolean(rules)}>
      <Controller
        rules={rules}
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <RadioGroup
              row={directionRow}
              value={value}
              onChange={(val) => {
                onChange(val.target.value);
                triggerValidationForField(name, val.target.value);
              }}
            >
              {generateRadioOptions(options, itemPerRow, radioVariant)}
            </RadioGroup>
            {Boolean(error?.message) && <FormHelperText>{error?.message}</FormHelperText>}
          </>
        )}
      />
    </DabaduRadio>
  </div>
);
